import { useEffect, useState, useCallback } from "react";
import { buildInfo } from "@/config/build";

interface AboutPanelProps {
  onClose?: () => void;
}

export function AboutPanel({ onClose }: AboutPanelProps): React.ReactNode {
  const [visible, setVisible] = useState(false);
  const [fps, setFps] = useState(0);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.ctrlKey && e.shiftKey && e.key === "I") {
        e.preventDefault();
        setVisible((v) => !v);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (!visible) return;
    let rafId: number;
    let last = performance.now();
    let frames = 0;
    function tick(now: number): void {
      frames++;
      if (now - last >= 1000) {
        setFps(frames);
        frames = 0;
        last = now;
      }
      rafId = requestAnimationFrame(tick);
    }
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [visible]);

  const handleClose = useCallback(() => {
    setVisible(false);
    onClose?.();
  }, [onClose]);

  if (!visible) return null;

  return (
    <div
      data-testid="about-panel"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0, 0, 0, 0.65)",
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        color: "#00ddff",
      }}
      onClick={handleClose}
    >
      <div
        style={{
          background: "rgba(0, 0, 0, 0.9)",
          border: "1px solid #00ddff",
          borderRadius: 8,
          padding: 32,
          minWidth: 360,
          maxWidth: 480,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: "0 0 20px", fontSize: 18, letterSpacing: 2 }}>
          Parallel Earth
        </h2>
        <table style={{ width: "100%", fontSize: 12, lineHeight: 2 }}>
          <tbody>
            <tr>
              <td style={{ color: "#4488ff", paddingRight: 16 }}>Version</td>
              <td>{buildInfo.version}</td>
            </tr>
            <tr>
              <td style={{ color: "#4488ff", paddingRight: 16 }}>Commit</td>
              <td>{buildInfo.sha}</td>
            </tr>
            <tr>
              <td style={{ color: "#4488ff", paddingRight: 16 }}>Build Date</td>
              <td>{new Date(buildInfo.date).toLocaleString()}</td>
            </tr>
            <tr>
              <td style={{ color: "#4488ff", paddingRight: 16 }}>Build #</td>
              <td>{buildInfo.number}</td>
            </tr>
            <tr>
              <td style={{ color: "#4488ff", paddingRight: 16 }}>Environment</td>
              <td>{buildInfo.environment}</td>
            </tr>
            <tr>
              <td style={{ color: "#4488ff", paddingRight: 16 }}>FPS</td>
              <td>{fps}</td>
            </tr>
            <tr>
              <td style={{ color: "#4488ff", paddingRight: 16 }}>Browser</td>
              <td>{navigator.userAgent.split("/")[0]}</td>
            </tr>
            <tr>
              <td style={{ color: "#4488ff", paddingRight: 16 }}>Resolution</td>
              <td>{`${window.innerWidth}x${window.innerHeight}`}</td>
            </tr>
          </tbody>
        </table>
        <p
          style={{
            margin: "16px 0 0",
            fontSize: 10,
            color: "rgba(255,255,255,0.35)",
            textAlign: "center",
          }}
        >
          Press CTRL+SHIFT+I or click anywhere to close
        </p>
      </div>
    </div>
  );
}
